import { z } from 'zod';
import {
  bookingStatusResponseSchema,
  createBookingBodySchema,
  creditPassSummarySchema,
  productTypeSchema,
} from '@yoga-tool/shared';

export const classSessionSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  location: z.string().nullable(),
  capacity: z.number().int().positive(),
  seatsAvailable: z.number().int().nonnegative(),
});

export type ClassSession = z.infer<typeof classSessionSchema>;

export const productSchema = z.object({
  id: z.string().uuid(),
  type: productTypeSchema,
  name: z.string(),
  description: z.string().nullable(),
  priceCents: z.number().int().nonnegative(),
  currency: z.string(),
  creditCount: z.number().int().positive().nullable(),
  validityDays: z.number().int().positive().nullable(),
});

export type Product = z.infer<typeof productSchema>;

export const createBookingResponseSchema = z.object({
  bookingId: z.string().uuid(),
  accessToken: z.string(),
  requiresPayment: z.boolean(),
  checkoutUrl: z.string().url().optional(),
});

export type CreateBookingResponse = z.infer<typeof createBookingResponseSchema>;

export const createPaymentResponseSchema = z.object({
  paymentId: z.string(),
  checkoutUrl: z.string().url(),
  status: z.string(),
});

export type CreatePaymentResponse = z.infer<typeof createPaymentResponseSchema>;

export const widgetReturnResponseSchema = bookingStatusResponseSchema.extend({
  accessToken: z.string(),
});

export type WidgetReturnResponse = z.infer<typeof widgetReturnResponseSchema>;

export const requestAccessCodeResponseSchema = z.object({
  message: z.string(),
});

export type RequestAccessCodeResponse = z.infer<typeof requestAccessCodeResponseSchema>;

export const verifyAccessCodeResponseSchema = z.object({
  accessToken: z.string(),
  customer: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
  }),
});

export type VerifyAccessCodeResponse = z.infer<typeof verifyAccessCodeResponseSchema>;

export { bookingStatusResponseSchema, createBookingBodySchema, creditPassSummarySchema };
export type { BookingStatusResponse, CreditPassSummary } from '@yoga-tool/shared';

export type BookingStep =
  | 'entry'
  | 'session'
  | 'product'
  | 'customer'
  | 'status'
  | 'redeemEmail'
  | 'redeemCode'
  | 'redeemPass'
  | 'redeemSession';

export type StatusView = 'confirmed' | 'processing' | 'failed' | 'expired';

export interface YogaBookingConfig {
  studioSlug: string;
  apiUrl?: string;
  /** Default true – show the built-in "Jetzt buchen" button */
  showTrigger?: boolean;
  /** CSS selector; all matches open the modal */
  triggerSelector?: string;
  mountElement?: HTMLElement;
}

export interface YogaBookingControls {
  open: () => void;
  close: () => void;
}

export interface YogaBookingHandle extends YogaBookingControls {
  destroy: () => void;
}

export interface BookingFlowState {
  step: BookingStep;
  sessionId: string | null;
  productId: string | null;
  bookingId: string | null;
  accessToken: string | null;
  statusView: StatusView | null;
  statusData: import('@yoga-tool/shared').BookingStatusResponse | null;
}
